import type { Row } from '@tanstack/react-table';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { IImportColumn, IPreviewRow } from '@feathr/blackbox';
import type { ITagListCellTag } from '@feathr/components';
import { TanStackTable } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import PreviewCard from './PreviewCard';
import previewTableColumns from './PreviewTableColumns';

import * as styles from './DataImportPreview.css';

interface IProps {
  columnMappers: IImportColumn[];
  rows: IPreviewRow[];
  tagIds: string[];
}

function DataImportPreview({ columnMappers, rows, tagIds }: IProps): JSX.Element {
  const { t } = useTranslation();
  const { Tags } = useStore();
  const [selected, setSelected] = useState({
    row: rows[0],
    index: 0,
  });

  const handleRowClick = useCallback((row: Row<IPreviewRow>): void => {
    setSelected({ row: row.original, index: row.index });
  }, []);

  const getRowClassName = useCallback(
    (row: Row<IPreviewRow>): string =>
      classNames({
        [styles.errorRow]: row.original.error,
        [styles.selectedRow]: row.index === selected.index,
      }),
    [selected.index],
  );

  /*
   * Resolved fresh on every render (not memoized): pending tags resolve their
   * names through MobX, and a memo would keep serving the pending snapshot
   * because its dependencies never change.
   */
  const tags: ITagListCellTag[] = tagIds.map((id) => {
    const tag = Tags.get(id);
    return { id, name: tag.isPending ? '' : tag.name, isPending: tag.isPending };
  });

  return (
    <div className={styles.root}>
      <h3>{t('Import preview')}</h3>
      <div className={styles.description}>
        <Trans t={t}>
          <p>
            Select a row below to see a preview of what the imported data will look like within
            Feathr. The preview only shows the first 10 rows of the import.
          </p>
        </Trans>
      </div>

      <TanStackTable<IPreviewRow>
        columns={previewTableColumns(t, columnMappers, tags)}
        getRowClassName={getRowClassName}
        idKey={'id'}
        isPaginated={false}
        items={rows}
        noDataText={t('No preview rows to display.')}
        onRowClick={handleRowClick}
      />
      {selected.row && <PreviewCard row={selected.row} tagIds={tagIds} />}
    </div>
  );
}

export default observer(DataImportPreview);
